// 携帯用の入社・契約解除フォーム（`/?entry=1`。スマホ前提・**ログイン必須**）。
//
// 経緯（本城さん 2026-08-28）: 徳永さんがPCでなく携帯で操作することがあり、
// ログインして事務所アプリで追加するより、公開フォームの方が操作しやすい。
//
// 設計（docs/37）:
//   - **送信＝そのままマスターシートへ登録**（本城さん要望 2026-08-28「承認なしで、そのまま
//     マスター登録して送信できるように」）。承認の一段を外した代わりに**ログイン必須**にした。
//     公開のままだと URL を知る人が誰でも従業員を登録できてしまうため
//   - 入力項目はアプリ内フォーム（PC版）と同じ。携帯で長くならないようセクションを折りたためる
//   - 契約解除は**マスタの在籍者から選ぶ**（氏名の文字入力では処理しない。別人の退職を防ぐ）
//   - 事務所アプリ（StoreProvider）には依存しない別レンダリング。ログインの枠だけ main.tsx で被せている

import React, { useEffect, useState } from "react";
import "./styles/driver-approval.css";
import logo from "./assets/hsd-logo.png";
import { submitEntry, fetchActiveDrivers, type EntryJoinInput } from "./lib/entryApi";

const PAGE_BG = "#eef0f4";
const FONT = "'Noto Sans JP', -apple-system, system-ui, sans-serif";

type Tab = "join" | "leave";

const EMPTY_JOIN: EntryJoinInput = {
  name: "",
  org: "個人",
  branch: "",
  email: "",
  contractFrom: "",
  invoiceNo: "",
  unitTakkyu: null,
  unitNekopos: null,
  unitDm: null,
  monthlyKeitai: null,
  monthlySharyo: null,
  monthlyAppli: null,
  shidoCount: null,
  hasGasCard: false,
  contractDocOk: false,
  atStyle: false,
  notes: "",
};

const todayIso = (): string => new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);

export function PublicEntry() {
  const [tab, setTab] = useState<Tab>("join");
  const [done, setDone] = useState<{ lines: string[] } | null>(null);

  if (done) {
    return (
      <Done
        title="送信しました"
        lines={done.lines}
        onBack={() => setDone(null)}
      />
    );
  }

  return (
    <Shell>
      <h1 style={{ fontSize: 18, margin: "10px 0 4px" }}>入社・契約解除のお届け</h1>
      <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7, margin: "0 0 14px" }}>
        送信すると、その場でマスターへ登録されます。入力した内容は、あとからパソコンの画面でも直せます。
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <TabButton active={tab === "join"} onClick={() => setTab("join")} label="入社" />
        <TabButton active={tab === "leave"} onClick={() => setTab("leave")} label="契約解除" />
      </div>

      {tab === "join" ? (
        <JoinForm onDone={(lines) => setDone({ lines })} />
      ) : (
        <LeaveForm onDone={(lines) => setDone({ lines })} />
      )}
    </Shell>
  );
}

// ===== 入社届 =====

function JoinForm({ onDone }: { onDone: (lines: string[]) => void }) {
  const [form, setForm] = useState<EntryJoinInput>(EMPTY_JOIN);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof EntryJoinInput>(k: K, v: EntryJoinInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("お名前を入力してください");
      return;
    }
    setBusy(true);
    setError(null);
    const r = await submitEntry({ kind: "join", join: { ...form, name: form.name.trim() } });
    setBusy(false);
    if (r.ok) {
      onDone([
        `${form.name.trim()} 様をマスターに登録しました。`,
        `管理番号は ${r.empId} です。`,
        ...(r.warnings || []),
        "単価などの内容は、パソコンの画面からいつでも直せます。",
      ]);
    } else {
      setError(r.error || "送信に失敗しました。少し時間をおいて再度お試しください。");
    }
  };

  return (
    <form onSubmit={submit}>
      {/* 基本情報（最初から開いた状態） */}
      <Section title="基本情報" open>
        <Field label="お名前（必須）">
          <input style={inputStyle} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="例: 山田 太郎" />
        </Field>
        <Field label="所属">
          <input style={inputStyle} value={form.org} onChange={(e) => set("org", e.target.value)} placeholder="個人 / 法人名" />
        </Field>
        <Field label="営業所">
          <input style={inputStyle} value={form.branch} onChange={(e) => set("branch", e.target.value)} placeholder="例: 佐川岩国 / 周南" />
        </Field>
        <Field label="メールアドレス">
          <input
            style={inputStyle}
            type="email"
            inputMode="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="例: taro@example.com"
          />
        </Field>
        <Field label="契約開始日">
          <input style={inputStyle} type="date" value={form.contractFrom} onChange={(e) => set("contractFrom", e.target.value)} />
        </Field>
        <Field label="登録番号（インボイス T+13桁）">
          <input
            style={inputStyle}
            value={form.invoiceNo}
            onChange={(e) => set("invoiceNo", e.target.value)}
            placeholder="T1234567890123（未登録なら空欄）"
          />
        </Field>
      </Section>

      {/* 単価（最初から開いた状態） */}
      <Section title="単価（円/個）" open>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <Field label="宅急便">
              <NumInput value={form.unitTakkyu} onChange={(v) => set("unitTakkyu", v)} />
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="ネコポス">
              <NumInput value={form.unitNekopos} onChange={(v) => set("unitNekopos", v)} />
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="DM便">
              <NumInput value={form.unitDm} onChange={(v) => set("unitDm", v)} />
            </Field>
          </div>
        </div>
      </Section>

      {/* 毎月の控除（たたんだ状態。使う方だけ開く） */}
      <Section title="毎月の控除（月額・任意）">
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <Field label="携帯リース">
              <NumInput value={form.monthlyKeitai} onChange={(v) => set("monthlyKeitai", v)} />
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="車両リース">
              <NumInput value={form.monthlySharyo} onChange={(v) => set("monthlySharyo", v)} />
            </Field>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <Field label="アプリ">
              <NumInput value={form.monthlyAppli} onChange={(v) => set("monthlyAppli", v)} />
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="指導料（件数）">
              <NumInput value={form.shidoCount} onChange={(v) => set("shidoCount", v)} />
            </Field>
          </div>
        </div>
        <Check
          label="ガソリンカードを貸与する"
          checked={form.hasGasCard}
          onChange={(v) => set("hasGasCard", v)}
          hint="毎月の利用額は、明細が発行されてから「月別控除入力」に入力します"
        />
      </Section>

      {/* その他 */}
      <Section title="その他">
        <Check label="契約書類 受領済み" checked={form.contractDocOk} onChange={(v) => set("contractDocOk", v)} />
        <Check label="株式会社AT-STYLE様 所属" checked={form.atStyle} onChange={(v) => set("atStyle", v)} />
        <Field label="備考">
          <input style={inputStyle} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="申し送りがあれば" />
        </Field>
      </Section>

      {error && <div style={{ color: "#dc2626", fontSize: 13, margin: "8px 0", whiteSpace: "pre-wrap" }}>{error}</div>}
      <button type="submit" disabled={busy} style={btnStyle(busy)}>
        {busy ? "送信中..." : "送信する"}
      </button>
      <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 12, lineHeight: 1.7 }}>
        ※ パソコンの入力画面と同じ項目です。送信するとその場でマスターに登録されます。
        あとから直せますので、分かる範囲でご入力ください。
      </p>
    </form>
  );
}

// ===== 契約解除届 =====
//
// 対象は**マスタの在籍者から選ぶ**（氏名の手入力では処理しない）。同姓同名・表記ゆれで
// 別の方を退職にすると、その方の支払が止まってしまうため。ログイン必須の画面なので、
// 在籍者の一覧をここで読んでも名簿が外に出ることはない。

function LeaveForm({ onDone }: { onDone: (lines: string[]) => void }) {
  const [drivers, setDrivers] = useState<{ id: string; name: string; branch: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [empId, setEmpId] = useState("");
  const [contractTo, setContractTo] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void fetchActiveDrivers().then((list) => {
      if (!alive) return;
      setDrivers(list);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const picked = drivers.find((d) => d.id === empId) || null;
  const filtered = q.trim()
    ? drivers.filter((d) => d.name.replace(/[\s　]/g, "").includes(q.trim().replace(/[\s　]/g, "")))
    : drivers;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!picked) {
      setError("対象の方を選んでください");
      return;
    }
    const future = contractTo > todayIso();
    const msg =
      `${picked.name} 様の契約終了日を ${contractTo} で登録します。` +
      (future ? "\n未来の日付のため「退職予定」となり、その日までは支払の対象のままです。" : "") +
      "\n\nよろしいですか？";
    if (!window.confirm(msg)) return;
    setBusy(true);
    setError(null);
    const r = await submitEntry({ kind: "leave", leave: { name: picked.name, contractTo, empId: picked.id } });
    setBusy(false);
    if (r.ok) {
      onDone([
        `${r.name || picked.name} 様の契約終了日を ${contractTo} で登録しました。`,
        r.scheduled
          ? "未来の日付のため「退職予定」です。その日までは支払の対象のままです。"
          : "この日をもって退職として扱われます。当月分の支払通知書は通常どおり作成されます。",
      ]);
    } else {
      setError(r.error || "送信に失敗しました。少し時間をおいて再度お試しください。");
    }
  };

  return (
    <form onSubmit={submit}>
      <Field label="お名前で絞り込む">
        <input style={inputStyle} value={q} onChange={(e) => setQ(e.target.value)} placeholder="例: 山田" />
      </Field>
      <Field label="対象の方（必須）">
        {loading ? (
          <div style={{ fontSize: 13, color: "#6b7280", padding: "6px 0" }}>読み込み中...</div>
        ) : (
          <select style={{ ...inputStyle, appearance: "auto" }} value={empId} onChange={(e) => setEmpId(e.target.value)}>
            <option value="">選択してください（在籍{drivers.length}名）</option>
            {filtered.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}（{d.branch || "—"}）
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="契約終了日（必須）">
        <input style={inputStyle} type="date" value={contractTo} onChange={(e) => setContractTo(e.target.value)} />
      </Field>
      {error && <div style={{ color: "#dc2626", fontSize: 13, margin: "8px 0", whiteSpace: "pre-wrap" }}>{error}</div>}
      <button type="submit" disabled={busy || loading} style={btnStyle(busy || loading)}>
        {busy ? "送信中..." : "送信する"}
      </button>
      <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 12, lineHeight: 1.7 }}>
        ※ 契約終了日が未来の日付の場合、その日までは支払の対象として扱われます（退職予定）。
        当月分の支払通知書は通常どおり作成されます。
      </p>
    </form>
  );
}

// ===== 共通パーツ（PublicRegister と同じ見た目） =====

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", background: PAGE_BG, fontFamily: FONT, color: "#1b2536", padding: "24px 16px" }}>
      <div style={{ maxWidth: 460, margin: "0 auto" }}>
        <img src={logo} alt="HS-deliverys" style={{ height: 22, marginBottom: 14 }} />
        <div style={{ background: "#fff", borderRadius: 14, padding: "20px 18px", boxShadow: "0 1px 3px rgba(0,0,0,.08)" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        padding: "11px 0",
        fontSize: 14,
        fontWeight: 700,
        fontFamily: FONT,
        color: active ? "#fff" : "#374151",
        background: active ? "#2563eb" : "#f3f4f6",
        border: "none",
        borderRadius: 10,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

// 携帯で20項目を1画面に並べると長すぎるので、セクションで折りたためるようにする。
// よく使う「基本情報」「単価」は最初から開き、控除・その他はたたんでおく。
function Section({ title, open, children }: { title: string; open?: boolean; children: React.ReactNode }) {
  return (
    <details
      open={open}
      style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "0 12px", marginBottom: 10, background: "#fcfcfd" }}
    >
      <summary
        style={{
          cursor: "pointer",
          fontSize: 13.5,
          fontWeight: 700,
          padding: "11px 0",
          listStyle: "none",
          color: "#374151",
        }}
      >
        {title}
      </summary>
      <div style={{ paddingBottom: 4 }}>{children}</div>
    </details>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <span style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}

// 数字入力：スピナー（▲▼）は出さず、スマホでは数字キーボードを出す
// （本城さん 2026-08-28「▲▼に手が当たって単価が変わってしまう」）
function NumInput({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  return (
    <input
      style={inputStyle}
      inputMode="numeric"
      value={value == null ? "" : String(value)}
      onChange={(e) => {
        // 単価は円単位の整数。小数点を許すと "1.2.3" のような入力で NaN になり、
        // 送信時に null（未入力）へ化けて単価が静かに落ちるため、数字だけ受ける
        const t = e.target.value.replace(/[^\d]/g, "");
        onChange(t === "" ? null : Number(t));
      }}
      placeholder="例: 150"
    />
  );
}

function Check({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <div style={{ margin: "4px 0 14px" }}>
      <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ width: 20, height: 20 }} />
        <span style={{ fontSize: 14 }}>{label}</span>
      </label>
      {hint && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, lineHeight: 1.6 }}>{hint}</div>}
    </div>
  );
}

function Done({ title, lines, onBack }: { title: string; lines: string[]; onBack: () => void }) {
  return (
    <Shell>
      <div style={{ textAlign: "center", padding: "10px 0" }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 10 }}>{title}</div>
        {lines.map((l, i) => (
          <p key={i} style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.8, margin: "2px 0" }}>
            {l}
          </p>
        ))}
        <button type="button" onClick={onBack} style={{ ...btnStyle(false), marginTop: 18 }}>
          続けて入力する
        </button>
      </div>
    </Shell>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "11px 12px",
  fontSize: 15,
  border: "1px solid #d1d5db",
  borderRadius: 9,
  outline: "none",
  fontFamily: FONT,
};

function btnStyle(disabled: boolean): React.CSSProperties {
  return {
    width: "100%",
    padding: "13px 0",
    fontSize: 15,
    fontWeight: 700,
    color: "#fff",
    background: disabled ? "#93b4f7" : "#2563eb",
    border: "none",
    borderRadius: 10,
    cursor: disabled ? "default" : "pointer",
    marginTop: 4,
    fontFamily: FONT,
  };
}
